import { useEffect, useRef } from "react";

// Two script variants provided by TicketsCandy for two different widget UX:
//   - "embed":    iframe embedded event page      (tc_widget.js + TCWidget)
//   - "checkout": inline checkout with tickets    (checkout-widget/loader.js + TCCheckout)
const VARIANTS = {
  embed: {
    scriptSrc: "https://ticketscandy.com/assets/javascript/tc_widget.js",
    scriptId: "ticketscandy-embed-script",
    globalName: "TCWidget",
    containerPrefix: "tc-container-iframe",
    init(TCWidget, { widgetId, containerId }) {
      TCWidget.createWidget({
        iframeContainerId: containerId,
        iframeUrl: `//ticketscandy.com/e/${widgetId}/embed`,
      });
    },
  },
  checkout: {
    scriptSrc: "https://ticketscandy.com/checkout-widget/loader.js",
    scriptId: "ticketscandy-checkout-script",
    globalName: "TCCheckout",
    containerPrefix: "tc-checkout-container",
    init(TCCheckout, { widgetId, containerId }) {
      TCCheckout.createWidget({
        widgetType: "checkout",
        eventId: String(widgetId),
        iframeContainerId: containerId,
        iframeContainerHeight: "auto",
      });
    },
  },
};

function loadScript(variant) {
  return new Promise((resolve, reject) => {
    if (window[variant.globalName]) return resolve(window[variant.globalName]);
    const existing = document.getElementById(variant.scriptId);
    if (existing) {
      existing.addEventListener("load", () => resolve(window[variant.globalName]));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.id = variant.scriptId;
    s.src = variant.scriptSrc;
    s.async = true;
    s.onload = () => resolve(window[variant.globalName]);
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

export default function TicketScandyWidget({ widgetId, widgetType = "embed" }) {
  const variant = VARIANTS[widgetType] || VARIANTS.embed;
  const containerId = `${variant.containerPrefix}-${widgetId}`;
  const initialized = useRef(false);

  useEffect(() => {
    if (!widgetId || initialized.current) return;
    let cancelled = false;
    loadScript(variant)
      .then((globalObj) => {
        if (cancelled || !globalObj) return;
        try {
          variant.init(globalObj, { widgetId, containerId });
          initialized.current = true;
        } catch (err) {
          console.error("TicketScandy init error:", err);
        }
      })
      .catch((err) => console.error("TicketScandy script load error:", err));
    return () => {
      cancelled = true;
    };
  }, [widgetId, containerId, variant]);

  if (!widgetId) return null;

  return (
    <div className="w-full" data-testid="ticketscandy-widget" data-widget-type={widgetType}>
      <div id={containerId} />
    </div>
  );
}
